import { useEffect, useRef } from 'react';
import { usePostHog } from 'posthog-js/react';
import { getAttributionProps } from '@/lib/attribution';
import { MARKETING_EVENTS } from '@/lib/analytics';

const MILESTONES = [25, 50, 75, 100] as const;

/**
 * Fires `marketing_scroll_depth` once for each 25/50/75/100% milestone the
 * visitor reaches on the page. Use on long marketing pages to measure how far
 * visitors get before bouncing.
 */
export function useScrollDepth(path: string) {
  const posthog = usePostHog();
  const fired = useRef<Set<number>>(new Set());

  useEffect(() => {
    fired.current = new Set();

    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - doc.clientHeight;
      if (scrollable <= 0) return;
      const pct = (window.scrollY / scrollable) * 100;
      for (const milestone of MILESTONES) {
        if (pct >= milestone && !fired.current.has(milestone)) {
          fired.current.add(milestone);
          posthog.capture(MARKETING_EVENTS.scrollDepth, {
            depth: milestone,
            path,
            ...getAttributionProps(),
          });
        }
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // capture the initial position (short pages reach 100% at once)
    return () => window.removeEventListener('scroll', onScroll);
  }, [posthog, path]);
}
